import React, { useState, useEffect } from 'react';
import { Modal } from '../../components/UI/Modal';
import { Input, Select } from '../../components/UI/Input';
import { Button } from '../../components/UI/Button';
import { formatDuration } from '../../utils/helpers';
import SafeIcon from '../../common/SafeIcon';

export const PhaseInstanceEditor = ({ isOpen, onClose, phase, onSave }) => {
  const [formData, setFormData] = useState({ name: '', tasks: [] });

  useEffect(() => {
    if (phase && isOpen) {
      // Deep clone to avoid direct mutation
      setFormData({
        name: phase.name || '',
        tasks: JSON.parse(JSON.stringify(phase.tasks || []))
      });
    }
  }, [phase, isOpen]);

  const handleTaskChange = (index, field, value) => {
    const newTasks = [...formData.tasks];
    newTasks[index] = { ...newTasks[index], [field]: value };
    setFormData({ ...formData, tasks: newTasks });
  };

  const handleTaskDurationChange = (index, part, value) => {
    const newTasks = [...formData.tasks];
    const currentTotal = newTasks[index].durationMinutes || 0;
    let h = Math.floor(currentTotal / 60);
    let m = currentTotal % 60;

    if (part === 'h') h = parseInt(value || 0);
    if (part === 'm') m = parseInt(value || 0);

    newTasks[index].durationMinutes = (h * 60) + m;
    setFormData({ ...formData, tasks: newTasks });
  };

  const totalMinutes = formData.tasks.reduce((s, t) => s + (t.durationMinutes || 0), 0);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Customize Phase Tasks">
      <div className="space-y-6">
        <div className="flex justify-between items-end gap-6">
          <Input 
            label="Phase Name" 
            value={formData.name} 
            onChange={e => setFormData({ ...formData, name: e.target.value })}
            className="flex-1"
          />
          <div className="bg-base-900 px-4 py-2 rounded-lg border border-base-700 text-center">
            <div className="text-[10px] text-gray-500 uppercase font-bold">Phase Effort</div>
            <div className="text-sm font-mono font-bold text-accent">{formatDuration(totalMinutes)}</div>
          </div>
        </div>

        <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
          <div className="text-xs font-bold text-gray-500 uppercase tracking-widest px-1">Tasks in Sequence</div>
          {formData.tasks.map((task, idx) => (
            <div key={task.id} className="bg-base-900/50 p-4 rounded-xl border border-base-700 space-y-3 group hover:border-base-600 transition-colors">
              <div className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-base-800 border border-base-700 flex items-center justify-center text-[10px] font-bold text-gray-500">
                  {idx + 1}
                </span>
                <input 
                  className="bg-transparent border-none outline-none text-sm font-semibold text-white flex-1 focus:ring-0 p-0"
                  value={task.name}
                  onChange={e => handleTaskChange(idx, 'name', e.target.value)}
                />
                <Select 
                  value={task.priority}
                  onChange={e => handleTaskChange(idx, 'priority', e.target.value)}
                  className="!w-28"
                  options={[
                    { label: 'Low', value: 'low' },
                    { label: 'Medium', value: 'medium' },
                    { label: 'High', value: 'high' },
                    { label: 'Critical', value: 'critical' }
                  ]}
                />
              </div>
              
              <div className="flex items-center gap-4 pl-9">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-gray-500 uppercase">Hours</span>
                  <input 
                    type="number"
                    min="0"
                    className="w-12 bg-base-800 border border-base-700 rounded px-1.5 py-0.5 text-xs text-center"
                    value={Math.floor((task.durationMinutes || 0) / 60)}
                    onChange={e => handleTaskDurationChange(idx, 'h', e.target.value)}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-gray-500 uppercase">Min</span>
                  <input 
                    type="number"
                    min="0"
                    max="59"
                    className="w-12 bg-base-800 border border-base-700 rounded px-1.5 py-0.5 text-xs text-center"
                    value={(task.durationMinutes || 0) % 60}
                    onChange={e => handleTaskDurationChange(idx, 'm', e.target.value)}
                  />
                </div>
                <div className="flex-1" />
                <button 
                  onClick={() => setFormData({ ...formData, tasks: formData.tasks.filter((_, i) => i !== idx) })}
                  className="p-1.5 text-gray-600 hover:text-red-400 hover:bg-red-400/10 rounded transition-all"
                >
                  <SafeIcon name="Trash2" className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(formData)}>Apply Changes</Button>
        </div>
      </div>
    </Modal>
  );
};